import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  ArrowLeft, 
  ArrowRight, 
  FileText, 
  Building2, 
  Calculator, 
  Globe2, 
  Briefcase,
  CheckCircle2,
  Clock,
  Users,
  Shield,
  Star
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { UserServiceService } from '@/integrations/supabase/services';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { DashboardLayout } from '@/components/DashboardLayout';
import '@/styles/animations.css';

interface Service {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
  category: 'residence' | 'business' | 'support';
  included: boolean;
}

const ServiceSelection = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  const services: Service[] = [
    {
      id: 'temporary_residence',
      name: 'Temporary Residence Card',
      description: 'Legal residence in Poland for up to 3 years with comprehensive support',
      icon: Clock,
      category: 'residence',
      included: false
    },
    {
      id: 'permanent_residence',
      name: 'Permanent Residence Card',
      description: 'Long-term residence with full rights and benefits',
      icon: Shield,
      category: 'residence',
      included: false
    },
    {
      id: 'eu_long_term',
      name: 'EU Long-term Residence Card',
      description: 'Residence permit valid across EU countries',
      icon: Globe2,
      category: 'residence',
      included: false
    },
    {
      id: 'company_registration',
      name: 'Company Registration',
      description: 'Register your business in Poland with full legal support',
      icon: Building2,
      category: 'business',
      included: false
    },
    {
      id: 'work_permit',
      name: 'Work Permit',
      description: 'Authorization to work legally in Poland',
      icon: Briefcase,
      category: 'business',
      included: false
    },
    {
      id: 'business_incubator',
      name: 'Business Incubator',
      description: 'Support for startups and new business ventures',
      icon: Star,
      category: 'business',
      included: false
    },
    {
      id: 'bookkeeping',
      name: 'Bookkeeping Services',
      description: 'Professional accounting and financial management',
      icon: Calculator,
      category: 'business',
      included: false
    },
    {
      id: 'translation',
      name: 'Translation Services',
      description: 'Professional document translation and interpretation',
      icon: FileText,
      category: 'support',
      included: false
    },
    {
      id: 'consultation',
      name: 'Legal Consultation',
      description: 'Expert legal advice and guidance',
      icon: Users,
      category: 'support',
      included: true
    }
  ];

  const categories = {
    residence: 'Residence Services',
    business: 'Business Services', 
    support: 'Support Services'
  };

  useEffect(() => {
    // Load previously selected services
    loadSelectedServices();
  }, []);

  const loadSelectedServices = async () => {
    if (!user) return;
    
    try {
      const userServices = await UserServiceService.getByUserId(user.uid);
      const serviceIds = userServices.map(service => service.service_id);
      
      if (serviceIds.length > 0) {
        setSelectedServices(serviceIds);
      } else {
        // Default to included services if no services are selected
        const defaultServices = services.filter(service => service.included).map(service => service.id);
        setSelectedServices(defaultServices);
      }
    } catch (error) {
      console.error('Error loading selected services:', error);
      // Default to included services on error
      const defaultServices = services.filter(service => service.included).map(service => service.id);
      setSelectedServices(defaultServices);
    }
  };

  const handleServiceToggle = (serviceId: string) => {
    setSelectedServices(prev => 
      prev.includes(serviceId) 
        ? prev.filter(id => id !== serviceId)
        : [...prev, serviceId]
    );
  };

  const handleSave = async () => {
    if (!user) return;

    setLoading(true);
    try {
      // Prepare services data for saving
      const servicesToSave = selectedServices.map(serviceId => {
        const service = services.find(s => s.id === serviceId);
        return {
          id: serviceId,
          name: service?.name || serviceId,
          category: service?.category || 'support'
        };
      });

      // Save to Supabase
      await UserServiceService.upsertServices(user.uid, servicesToSave);
      
      toast({
        title: "Services Selected",
        description: `You have selected ${selectedServices.length} services.`,
      });

      // Navigate to next step
      navigate('/your-package');
    } catch (error) {
      console.error('Error saving services:', error);
      toast({
        title: "Error",
        description: "Failed to save service selection. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/dashboard');
  };

  const handleContinue = () => {
    navigate('/your-package');
  };

  const getServicesByCategory = (category: keyof typeof categories) => {
    return services.filter(service => service.category === category);
  };

  return (
    <DashboardLayout pageTitle="Service Selection">
      <div className="space-y-8">
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-foreground mb-4 font-alumni">Select Your Services</h1>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto font-hind">
            Choose the specific services you need for your Polish residence and business needs
          </p>
        </div>

        {/* Service Categories */}
        {Object.entries(categories).map(([categoryKey, categoryName]) => {
          const categoryServices = getServicesByCategory(categoryKey as keyof typeof categories);
          
          return (
            <div key={categoryKey} className="space-y-4">
              <h2 className="text-2xl font-bold text-foreground font-alumni">{categoryName}</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {categoryServices.map((service) => {
                  const IconComponent = service.icon;
                  const isSelected = selectedServices.includes(service.id);
                  
                  return (
                    <Card 
                      key={service.id}
                      className={`transition-all duration-300 hover:shadow-lg backdrop-blur-sm cursor-pointer group
                        ${isSelected ? 'ring-2 ring-primary bg-primary/5' : ''}
                        ${service.included ? 'border-green-200 bg-green-50/50' : ''}
                      `}
                      onClick={() => handleServiceToggle(service.id)}
                    >
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center space-x-3">
                            <div className={`p-2 rounded-lg ${isSelected ? 'bg-primary/20' : 'bg-muted/50'} group-hover:bg-primary/20 transition-colors`}>
                              <IconComponent className={`h-5 w-5 ${isSelected ? 'text-primary' : 'text-muted-foreground'} group-hover:text-primary transition-colors`} />
                            </div>
                            <div>
                              <CardTitle className="text-lg font-alumni">{service.name}</CardTitle>
                              {service.included && (
                                <Badge variant="secondary" className="text-xs bg-green-100 text-green-700 border-green-200">
                                  Included
                                </Badge>
                              )}
                            </div>
                          </div>
                          <Checkbox 
                            checked={isSelected}
                            onChange={() => handleServiceToggle(service.id)}
                            className="mt-1"
                          />
                        </div>
                      </CardHeader>
                      <CardContent className="pt-0">
                        <CardDescription className="font-hind text-sm">
                          {service.description}
                        </CardDescription>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Selection Summary */}
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-foreground font-alumni">Selected Services</h3>
                <p className="text-muted-foreground font-hind">
                  {selectedServices.length} service{selectedServices.length !== 1 ? 's' : ''} selected
                </p>
              </div>
              <div className="flex items-center space-x-2">
                <CheckCircle2 className="h-5 w-5 text-primary" />
                <span className="text-sm font-medium text-primary">
                  {selectedServices.length} Selected
                </span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row sm:justify-between items-center mt-8 pt-6 border-t border-border gap-4">
          <Button
            variant="outline"
            onClick={handleBack}
            className="w-full sm:w-auto flex items-center justify-center gap-2 order-3 sm:order-1"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>Back to Dashboard</span>
          </Button>

          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto order-1 sm:order-2">
            <Button
              onClick={handleSave}
              disabled={loading || selectedServices.length === 0}
              className="w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {loading ? 'Saving...' : 'Save Selection'}
            </Button>
            
            <Button
              onClick={handleContinue}
              className="w-full sm:w-auto flex items-center justify-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <span>Continue</span>
              <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ServiceSelection;
